import { create } from "@bufbuild/protobuf";
import { Code, ConnectError, type CallOptions, type Client } from "@connectrpc/connect";
import type { Workspace } from "#gen/identities/v1/identities_pb.js";
import { IntegrationCategory, type Connection, type Marketplace, type WorkspaceTenant } from "#gen/integrations/v1/integration_pb.js";
import { IntegrationFilterSchema, IntegrationsService } from "#gen/integrations/v1/service_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";

export class IntegrationsClient {
	private client: Client<typeof IntegrationsService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.integrations || "/", IntegrationsService, options);
	}

	async getMarketplaceURL(workspace: Workspace): Promise<string> {
		try {
			const res = await this.client.getMarketplaceURL({ orgId: workspace.orgId, workspaceId: workspace.id, workspaceName: workspace.name });
			return res.marketplaceUrl;
		} catch (e) {
			// for now just return an unset url if the receiver has not been set up
			if (e instanceof ConnectError && e.code == Code.NotFound) {
				return "";
			}
			throw handleError(e);
		}
	}

	async getMarketplace(workspace: Workspace, options?: CallOptions): Promise<Marketplace | undefined> {
		try {
			const res = await this.client.getMarketplace(
				{
					orgId: workspace.orgId,
					workspaceId: workspace.id,
					workspaceName: workspace.name,
					filter: create(IntegrationFilterSchema, {
						category: IntegrationCategory.AGENT,
					}),
				},
				options,
			);
			return res.marketplace;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getIntegration(workspaceTenantId: string): Promise<WorkspaceTenant> {
		try {
			const res = await this.client.getWorkspaceTenant({ workspaceTenantId });
			return res.workspaceTenant!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listWorkspaceConnections(orgId: string, workspaceId: string): Promise<Connection[]> {
		try {
			const res = await this.client.listWorkspaceConnections({
				orgId,
				workspaceId,
			});
			return res.connections;
		} catch (e) {
			throw handleError(e);
		}
	}
}
